import type {
  FetchOrganizationMemberByIdArgs,
  FetchOrganizationMembersArgs,
  UpdateOrganizationMemberArgs,
} from './types/organizationMembers.types';
import type {
  ApiOrganizationMember,
  UiOrganizationMember,
} from '../schemas/organizationUser/organization.member.schema';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';

import { mapOrganizationMemberFromApi } from './mappers/organizationMembers.mappers';
import { destroy, patch, post, put, get } from './axios';

export async function fetchOrganizationMemberById({
  id,
  organizationId,
}: FetchOrganizationMemberByIdArgs) {
  const response = await get<BaseApiResponse<ApiOrganizationMember>>(
    `/v2/organizations/${organizationId}/members/${id}`,
  );
  return mapOrganizationMemberFromApi(response?.data);
}

/** Owner adds (`enabled`) or removes the Reception (`front_desk`) role. */
export async function setOrganizationMemberFrontDesk({
  enabled,
  id,
  organizationId,
}: FetchOrganizationMemberByIdArgs & { enabled: boolean }) {
  const url = `/v2/organizations/${organizationId}/members/${id}/front-desk`;
  return enabled ? post<unknown>(url, {}) : destroy(url);
}

export async function fetchOrganizationMembers({
  filters,
  organizationId,
}: FetchOrganizationMembersArgs) {
  const response = await get<BaseApiPaginatedResponse<ApiOrganizationMember[]>>(
    `/v2/organizations/${organizationId}/members`,
    {
      query: filters,
    },
  );

  return {
    data: (response?.data || []).flatMap(
      (d) => mapOrganizationMemberFromApi(d) ?? [],
    ) as UiOrganizationMember[],
    message: response?.message || '',
    status: response?.status || 200,
    total: response?.total || 0,
  };
}

// WS1 — disconnects any non-client member (TRAINER/STAFF/OWNER) from the
// organization. The BE enforces the last-owner guard (409 LAST_OWNER).
export async function disconnectOrganizationMemberById({
  id,
  organizationId,
}: FetchOrganizationMemberByIdArgs) {
  await put(`/v2/organizations/${organizationId}/members/disconnect/${id}`);
  return true;
}

// F8 — promotes an ACTIVE TRAINER/STAFF member to OWNER (org-scoped
// organization_owner role). Idempotent no-op if already OWNER. The BE
// response is the raw OrganizationUser relation row (not the member profile
// shape) — the caller refetches the member profile via query invalidation
// instead of mapping this response.
export async function promoteOrganizationMemberToOwner({
  id,
  organizationId,
}: FetchOrganizationMemberByIdArgs) {
  await put(`/v2/organizations/${organizationId}/members/${id}/promote-owner`);
}

export async function updateOrganizationMember({
  id,
  address,
  contact,
  name,
  organizationId,
  roleIds,
  socialMedia,
  status,
  userSettings,
}: UpdateOrganizationMemberArgs) {
  const response = await patch<ApiOrganizationMember>(
    `/v2/organizations/${organizationId}/members/${id}`,
    { address, contact, name, roleIds, socialMedia, status, userSettings },
  );
  return mapOrganizationMemberFromApi(response?.data);
}
